import {useNavigate, useParams} from 'react-router-dom'
import {TextValidator, ValidatorForm} from "react-material-ui-form-validator"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import React, {useEffect, useRef, useState} from "react"
import {getFieldErrorParams, getInputValue, processEmojiInput} from "../../../utils"
import AdminContainer from "../../../components/admin/AdminContainer"
import {FormHelperText, Grid} from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import {useGlobalContext} from "../../../contexts/global_context"
import {BACKEND_URL, BRAND_NAME} from "../../../data/config"
import {Helmet} from "react-helmet";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";

const OrderDiscountCreateUpdateAdminPage = () => {
  const navigate = useNavigate()
  let {uid} = useParams()
  let form = useRef()
  let {sendAjax} = useGlobalContext()
  let [fieldErrors, setFieldErrors] = useState({})
  let [categories, setCategories] = useState([])

  useEffect(() => {
    // Fetch categories for the multi-select
    sendAjax({
      url: `${BACKEND_URL}/api/v1/pkg/admin/categories/`,
      method: 'get',
      data: {page_size: 1000},
    }).then((r) => {
      if (r.data.status_code === 200) {
        setCategories(r.data.data.results)
      }
    })

    if (uid) {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/commerce/order-discounts/${uid}/`,
        method: 'get',
        data: {},
      }).then((r) => {
        if (r.data.status_code === 200) {
          let orderDiscount = r.data.data
          setFormValues({
            title: orderDiscount.title || '',
            code: orderDiscount.code || '',
            email: orderDiscount.email || '',
            social: orderDiscount.social || '',
            category_uids: orderDiscount.category_uids || [],
            discount_type: orderDiscount.discount_type || '',
            value: orderDiscount.value || '',
            expire_date: orderDiscount.expire_date ? orderDiscount.expire_date.split('T')[0] : '',
            max_usage: orderDiscount.max_usage || '',
            max_discount_price: orderDiscount.max_discount_price || '',
            status: orderDiscount.status || '',
            usage_count: orderDiscount.usage_count || 0,
            condition: orderDiscount.condition || '',
            message_emoji: orderDiscount.message_emoji || '',
            message_body: orderDiscount.message_body || '',
          })
        }
      })
    }
  }, [])

  let [formValues, setFormValues] = useState({
    title: '',
    code: '',
    email: '',
    social: '',
    category_uids: [],
    discount_type: '',
    value: '',
    expire_date: '',
    max_usage: '',
    max_discount_price: '',
    status: '',
    usage_count: 0,
    condition: '',
    message_emoji: '🎁',
    message_body: '',
  })

  const afterSubmit = (r) => {
    if (r.data.status_code === 200) {
      navigate("/admin/order-discounts/")
    } else if (r.data.status_code === 400) {
      setFieldErrors(r.data.data)
    }
  }

  const handleSubmit = () => {
    // Convert date to datetime format if expire_date is provided
    const submitData = { ...formValues }
    if (submitData.expire_date) {
      submitData.expire_date = `${submitData.expire_date}T23:59:59Z`
    }

    // Convert empty strings to null for choice fields
    const choiceFields = ['social', 'discount_type', 'status', 'condition', 'max_usage', 'expire_date']
    choiceFields.forEach(field => {
      if (submitData[field] === '') {
        submitData[field] = null
      }
    })

    if (uid) {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/commerce/order-discounts/${uid}/`,
        method: 'put',
        data: submitData,
      }).then(
        afterSubmit
      )
    } else {
      sendAjax({
        url: `${BACKEND_URL}/api/v1/commerce/order-discounts/`,
        method: 'post',
        data: submitData,
      }).then(
        afterSubmit
      )
    }
  }

  const handleChange = (event) => {
    let value = getInputValue(event.target);
    
    // Special handling for message_emoji field to limit to one emoji only
    if (event.target.name === 'message_emoji') {
      value = processEmojiInput(value);
    }
    
    setFormValues({...formValues, [event.target.name]: value})
    setFieldErrors({})
  }

  const handleCategoryChange = (event) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setFormValues({...formValues, category_uids: value})
    setFieldErrors({})
  }

  return (
    <AdminContainer>
      <Helmet>
        <title>Order Discounts - {BRAND_NAME}</title>
      </Helmet>

      <ValidatorForm ref={form} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextValidator
              label="Title *"
              onChange={handleChange}
              name="title"
              value={formValues.title}
              validators={['required']}
              errorMessages={['Title is required']}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'title')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Discount Code (Optional)"
              onChange={handleChange}
              name="code"
              value={formValues.code}
              validators={[]}
              errorMessages={[]}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'code')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Email (Optional)"
              onChange={handleChange}
              name="email"
              value={formValues.email}
              validators={[]}
              errorMessages={[]}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'email')}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Categories (Optional)</InputLabel>
              <Select
                multiple
                value={formValues.category_uids}
                onChange={handleCategoryChange}
                input={<OutlinedInput label="Categories (Optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const category = categories.find(cat => cat.uid === value);
                      return (
                        <Chip key={value} label={category?.title || value} />
                      );
                    })}
                  </Box>
                )}
              >
                {categories.map((category) => (
                  <MenuItem key={category.uid} value={category.uid}>
                    {category.title}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.category_uids && (
                <FormHelperText error>{fieldErrors.category_uids}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              select
              label="Social (Optional)"
              onChange={handleChange}
              name="social"
              value={formValues.social}
              validators={[]}
              errorMessages={[]}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'social')}
            >
              <MenuItem value="">Select Social</MenuItem>
              <MenuItem value={10}>Instagram</MenuItem>
              <MenuItem value={20}>TikTok</MenuItem>
              <MenuItem value={30}>Clubhouse</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              select
              label="Discount Type"
              onChange={handleChange}
              name="discount_type"
              value={formValues.discount_type}
              validators={['required']}
              errorMessages={['This field is required']}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'discount_type')}
            >
              <MenuItem value="">Select Type</MenuItem>
              <MenuItem value={10}>Fixed price</MenuItem>
              <MenuItem value={20}>Percentage</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Value"
              onChange={handleChange}
              name="value"
              value={formValues.value}
              validators={['required']}
              errorMessages={['This field is required']}
              fullWidth
              type="number"
              {...getFieldErrorParams(fieldErrors, 'value')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Expire Date (Optional)"
              onChange={handleChange}
              name="expire_date"
              value={formValues.expire_date}
              validators={[]}
              errorMessages={[]}
              fullWidth
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              {...getFieldErrorParams(fieldErrors, 'expire_date')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Max Usage (Optional)"
              onChange={handleChange}
              name="max_usage"
              value={formValues.max_usage}
              validators={[]}
              errorMessages={[]}
              fullWidth
              type="number"
              {...getFieldErrorParams(fieldErrors, 'max_usage')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Usage Count"
              onChange={handleChange}
              name="usage_count"
              value={formValues.usage_count}
              validators={[]}
              errorMessages={[]}
              fullWidth
              type="number"
              {...getFieldErrorParams(fieldErrors, 'usage_count')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Max Discount Price (Optional)"
              onChange={handleChange}
              name="max_discount_price"
              value={formValues.max_discount_price}
              validators={[]}
              errorMessages={[]}
              fullWidth
              type="number"
              {...getFieldErrorParams(fieldErrors, 'max_discount_price')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              select
              label="Status"
              onChange={handleChange}
              name="status"
              value={formValues.status}
              validators={['required']}
              errorMessages={['This field is required']}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'status')}
            >
              <MenuItem value="">Select Status</MenuItem>
              <MenuItem value={10}>Active</MenuItem>
              <MenuItem value={20}>Inactive</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              select
              label="Condition (Optional)"
              onChange={handleChange}
              name="condition"
              value={formValues.condition}
              validators={[]}
              errorMessages={[]}
              fullWidth
              {...getFieldErrorParams(fieldErrors, 'condition')}
            >
              <MenuItem value="">Select Condition</MenuItem>
              <MenuItem value={10}>First username order</MenuItem>
            </TextValidator>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextValidator
              label="Message Emoji"
              onChange={handleChange}
              name="message_emoji"
              value={formValues.message_emoji}
              validators={['required', 'maxStringLength:10']}
              errorMessages={['This field is required', 'Maximum 10 characters allowed']}
              fullWidth
              // inputProps={{ maxLength: 1 }}
              {...getFieldErrorParams(fieldErrors, 'message_emoji')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextValidator
              label="Message Body"
              onChange={handleChange}
              name="message_body"
              value={formValues.message_body}
              validators={['required']}
              errorMessages={['This field is required']}
              fullWidth
              multiline
              rows={4}
              {...getFieldErrorParams(fieldErrors, 'message_body')}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{display: 'flex', gap: 2}}>
              <Button type="submit" variant="contained">
                {uid ? 'Update' : 'Create'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/order-discounts/")}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </ValidatorForm>
    </AdminContainer>
  )
}

export default OrderDiscountCreateUpdateAdminPage